'use client';

import { MotionConfig } from 'framer-motion';
import { type ReactNode, useCallback, useMemo } from 'react';
import { recentSearches } from './core/recentStore';
import { SearchProvider } from './core/SearchContext';
import type { RecentSearch } from './core/types';
import { DefaultView } from './DefaultView';
import type { SearchState } from './hooks/useSearchState';
import { ResultsView } from './ResultsView';
import { SearchTabs } from './shared/SearchTabs';

const renderBare = (content: ReactNode) => content;

interface SearchBodyProps {
  query: string;
  state: SearchState;
  onQueryChange: (query: string) => void;
  /** Called once a result or a section's page is opened. */
  onClose: () => void;
  /** Bumped to play the lists' entrance again — each time the panel opens. */
  entranceKey?: number;
  /** The frame the lists scroll in; the phone's search scrolls the dialog itself. */
  renderList?: (content: ReactNode) => ReactNode;
}

/**
 * What the search shows under the field: the tabs, always, then a little of
 * everything while the field is (nearly) empty, the results once there is a
 * query to look for.
 */
export function SearchBody({
  query,
  state,
  onQueryChange,
  onClose,
  entranceKey = 0,
  renderList = renderBare,
}: SearchBodyProps) {
  const { tab, setTab, searching, results, counts } = state;

  const select = useCallback(
    (item: RecentSearch) => {
      recentSearches.push(item);
      onClose();
    },
    [onClose],
  );
  const context = useMemo(
    () => ({ query, select, setQuery: onQueryChange, close: onClose }),
    [query, select, onQueryChange, onClose],
  );

  // A new view mounts afresh and plays its entrance; typing within the results only swaps rows.
  const viewKey = `${entranceKey}:${searching ? 'results' : 'default'}:${tab}`;

  return (
    <SearchProvider value={context}>
      <MotionConfig reducedMotion="user">
        <SearchTabs active={tab} counts={counts} onSelect={setTab} />
        {renderList(
          <div key={viewKey} className="flex flex-1 flex-col gap-1.5">
            {searching ? (
              <ResultsView results={results} tab={tab} onTabChange={setTab} />
            ) : (
              <DefaultView tab={tab} />
            )}
          </div>,
        )}
      </MotionConfig>
    </SearchProvider>
  );
}
